import { createContext, useContext, useEffect, useRef, useState } from "react";
import { usePunditUserContext } from "@circle-react/contexts";
import {
  HELP_WIDGET_IS_EXPANDED_LOCAL_STORAGE_KEY,
  HELP_WIDGET_IS_OPEN_LOCAL_STORAGE_KEY,
  HELP_WIDGET_IS_SIDEBAR_OPEN_LOCAL_STORAGE_KEY,
} from "../constants";

interface HelpWidgetContextValue {
  collapseSideBar: () => void;
  collapseWidget: () => void;
  currentSpaceProperties: {
    name: string;
    emoji: string;
    custom_emoji_url: string;
    custom_emoji_dark_url: string;
  };
  expandSideBar: () => void;
  expandWidget: () => void;
  isExpanded: boolean;
  isOpen: boolean;
  isSidebarOpen: boolean;
  openWidget: () => void;
  setCurrentSpaceProperties: (properties: {
    name: string;
    emoji: string;
    custom_emoji_url: string;
    custom_emoji_dark_url: string;
  }) => void;
  shouldDisplayOnboarding: boolean;
  toggleWidget: (options?: { shouldTriggerOnWidgetClose?: boolean }) => void;
}

export const HelpWidgetContext = createContext<HelpWidgetContextValue>({
  isOpen: false,
  toggleWidget: () => {},
  openWidget: () => {},
  isExpanded: false,
  isSidebarOpen: false,
  expandWidget: () => {},
  collapseWidget: () => {},
  currentSpaceProperties: {
    name: "",
    emoji: "",
    custom_emoji_url: "",
    custom_emoji_dark_url: "",
  },
  setCurrentSpaceProperties: () => {},
  expandSideBar: () => {},
  collapseSideBar: () => {},
  shouldDisplayOnboarding: false,
});

HelpWidgetContext.displayName = "HelpWidgetContext";

export const useHelpWidgetContext = () => {
  const context = useContext(HelpWidgetContext);
  if (!context) {
    throw new Error(
      "useHelpWidgetContext must be used within a HelpWidgetContextProvider",
    );
  }
  return context;
};

interface HelpWidgetProviderProps {
  children: React.ReactNode;
}

export const HelpWidgetContextProvider = ({
  children,
}: HelpWidgetProviderProps) => {
  const { isViewOnlyMasquerading, currentCommunityMember } =
    usePunditUserContext();
  const onWidgetClose = useRef<(() => void) | null>(null);

  const shouldDisplayOnboarding =
    currentCommunityMember?.preferences?.has_seen_widget_onboarding === false; // Strict equality is on purpose here, we want to display onboarding only when `has_seen_widget_onboarding` is equal to false

  const [isOpen, setIsOpen] = useState(() => {
    if (isViewOnlyMasquerading) return false;
    const storedIsOpen = localStorage.getItem(
      HELP_WIDGET_IS_OPEN_LOCAL_STORAGE_KEY,
    );
    return storedIsOpen === "true";
  });

  const [isExpanded, setIsExpanded] = useState(() => {
    const storedIsExpanded = localStorage.getItem(
      HELP_WIDGET_IS_EXPANDED_LOCAL_STORAGE_KEY,
    );
    return storedIsExpanded === "true";
  });

  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const storedIsSidebarOpen = localStorage.getItem(
      HELP_WIDGET_IS_SIDEBAR_OPEN_LOCAL_STORAGE_KEY,
    );
    return storedIsSidebarOpen === "true";
  });

  useEffect(() => {
    localStorage.setItem(
      HELP_WIDGET_IS_EXPANDED_LOCAL_STORAGE_KEY,
      isExpanded.toString(),
    );
  }, [isExpanded]);

  useEffect(() => {
    localStorage.setItem(
      HELP_WIDGET_IS_OPEN_LOCAL_STORAGE_KEY,
      isOpen.toString(),
    );
  }, [isOpen]);

  useEffect(() => {
    localStorage.setItem(
      HELP_WIDGET_IS_SIDEBAR_OPEN_LOCAL_STORAGE_KEY,
      isSidebarOpen.toString(),
    );
  }, [isSidebarOpen]);

  const [currentSpaceProperties, setCurrentSpaceProperties] = useState({
    name: "",
    emoji: "",
    custom_emoji_url: "",
    custom_emoji_dark_url: "",
  });

  const expandWidget = () => {
    setIsExpanded(true);
  };

  const collapseWidget = () => {
    setIsExpanded(false);
  };

  const expandSideBar = () => {
    setIsSidebarOpen(true);
  };

  const collapseSideBar = () => {
    setIsSidebarOpen(false);
  };

  const toggleWidget = ({ shouldTriggerOnWidgetClose = false } = {}) => {
    if (isViewOnlyMasquerading) {
      return;
    }

    if (
      shouldTriggerOnWidgetClose &&
      isOpen &&
      onWidgetClose.current !== null
    ) {
      onWidgetClose.current();
    } else {
      const willBeOpen = !isOpen;
      setIsOpen(willBeOpen);
    }
  };

  const openWidget = () => {
    setIsOpen(true);
  };

  return (
    <HelpWidgetContext.Provider
      value={{
        isOpen,
        toggleWidget,
        openWidget,
        isExpanded,
        isSidebarOpen,
        expandWidget,
        collapseWidget,
        currentSpaceProperties,
        setCurrentSpaceProperties,
        expandSideBar,
        collapseSideBar,
        shouldDisplayOnboarding,
      }}
    >
      {children}
    </HelpWidgetContext.Provider>
  );
};
